import { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Hash, Loader2, Sparkles, Copy, Check } from 'lucide-react';

const API = '/api';

const CAT_COLORS = {
  Branded: 'bg-violet-100 text-violet-700',
  Industry: 'bg-blue-100 text-blue-700',
  Trending: 'bg-amber-100 text-amber-700',
  Niche: 'bg-emerald-100 text-emerald-700',
};

export default function HashtagSuggest({ contentText, topic, platforms, onApply }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState('');

  const suggest = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/social/hashtags/suggest`, {
        content_text: contentText, topic, platforms
      }, { withCredentials: true });
      setResult(data);
    } catch {
      toast.error('Failed to suggest hashtags');
    } finally { setLoading(false); }
  };

  const copySet = (platform, tags) => {
    navigator.clipboard.writeText(tags);
    setCopied(platform);
    setTimeout(() => setCopied(''), 2000);
    toast.success(`${platform} hashtags copied`);
  };

  const applySet = (tags) => {
    onApply?.(tags);
    toast.success('Hashtags applied');
  };

  return (
    <div className="space-y-3" data-testid="hashtag-suggest">
      <Button
        size="sm" variant="outline"
        onClick={suggest}
        disabled={loading || (!contentText && !topic)}
        data-testid="suggest-hashtags-btn"
      >
        {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
        Suggest Hashtags
      </Button>

      {result && (
        <div className="space-y-3 p-4 bg-slate-50 rounded-lg border border-slate-200">
          {/* Grouped hashtags */}
          {result.hashtag_groups?.map((group, i) => (
            <div key={i}>
              <div className="flex items-center gap-2 mb-1.5">
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${CAT_COLORS[group.category] || 'bg-slate-100 text-slate-600'}`}>
                  {group.category}
                </span>
                <span className="text-[10px] text-slate-400">{group.reason}</span>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {group.hashtags?.map((tag, j) => (
                  <Badge key={j} variant="outline" className="text-xs cursor-pointer hover:bg-indigo-50" onClick={() => onApply?.(tag)}>
                    <Hash className="h-3 w-3 mr-0.5" />{tag.replace('#', '')}
                  </Badge>
                ))}
              </div>
            </div>
          ))}

          {/* Platform-specific sets */}
          {result.recommended_set && (
            <div className="pt-3 border-t border-slate-200 space-y-2">
              <p className="text-xs font-semibold text-slate-600">Platform-Optimized Sets</p>
              {Object.entries(result.recommended_set).map(([platform, tags]) => (
                <div key={platform} className="flex items-center justify-between gap-2 p-2 bg-white rounded border border-slate-100">
                  <div className="min-w-0">
                    <span className="text-xs font-medium text-slate-500 capitalize">{platform}:</span>
                    <span className="text-xs text-blue-600 ml-1">{tags}</span>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      size="sm" variant="ghost"
                      className="h-7 px-2"
                      onClick={() => copySet(platform, tags)}
                    >
                      {copied === platform ? <Check className="h-3 w-3 text-emerald-500" /> : <Copy className="h-3 w-3" />}
                    </Button>
                    <Button
                      size="sm" variant="ghost"
                      className="h-7 px-2 text-xs text-indigo-600"
                      onClick={() => applySet(tags)}
                      data-testid={`apply-hashtags-${platform}`}
                    >
                      Apply
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Avoid list */}
          {result.avoid?.length > 0 && (
            <div className="pt-2">
              <p className="text-[10px] text-red-500 font-medium">Avoid: {result.avoid.join(', ')}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
